import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Trophy, Users, FloppyDisk, GameController, Lightning, Crown, Globe, Flag,
  GlobeHemisphereWest, Medal, ArrowCounterClockwise, SoccerBall, Lock,
} from '@phosphor-icons/react';
import { JwflLogo } from '../components/EwflLogo';

export const HomePage = ({ gameState, managerState, careerState, onNewGame, onResetLeague, onResetManager, onResetCareer }) => {
  const navigate = useNavigate();
  const hasLeagueGame = gameState?.teams?.length > 0;
  const hasManagerGame = managerState?.managerMode?.teamId;
  const hasCareerGame = !!careerState?.playerId;

  const startNewLeague = () => {
    if (hasLeagueGame && !window.confirm('Start a NEW J.League save? This will erase your current progress (Season ' + gameState.season + ', Week ' + gameState.currentWeek + ').')) return;
    onResetLeague?.();
    onNewGame();
    navigate('/league');
  };

  const startNewManager = () => {
    if (hasManagerGame && !window.confirm('Start a NEW Road to Glory save? This will erase your current career.')) return;
    onResetManager?.();
    navigate('/manager');
  };

  const startNewCareer = () => {
    if (hasCareerGame && !window.confirm('Start a NEW Player Career? This will erase your current player.')) return;
    onResetCareer?.();
    navigate('/player-career/select');
  };

  return (
    <div className="min-h-screen hero-bg relative overflow-hidden">
      <div className="animated-bg-layer animated-bg-1" aria-hidden="true" />
      <div className="animated-bg-layer animated-bg-2" aria-hidden="true" />
      <div className="animated-bg-grain" aria-hidden="true" />
      <div className="animated-embers" aria-hidden="true">
        {Array.from({ length: 24 }).map((_, i) => (
          <span key={i} className={`ember ember-${i % 6}`} style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i * 0.7) % 8}s`, animationDuration: `${8 + (i % 5) * 2}s` }} />
        ))}
      </div>

      {/* Japanese kanji watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
        <div className="opacity-[0.04] text-[#00E5FF] font-black"
          style={{ fontSize: '38vmin', letterSpacing: '0.05em', textShadow: '0 0 80px rgba(0,229,255,0.45)' }}>
          日本
        </div>
      </div>

      <div className="hero-overlay min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-8 relative z-10 ewfl-hud-grid">
        <div className="mb-10 text-center">
          <div className="mx-auto flex items-center justify-center mb-4 ewfl-float"
            style={{ filter: 'drop-shadow(0 0 50px rgba(0,229,255,0.55))' }}>
            <JwflLogo size={96} />
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-wideer uppercase ewfl-grad-text" data-testid="game-title">
            JWFL
          </h1>
          <p className="text-[#7A86B5] text-lg mt-2 tracking-[0.22em] uppercase">Japan World Football League</p>
          <p className="text-[#4A5278] text-[10px] mt-1 font-mono tracking-[0.5em] uppercase">日本 · ワールド · フットボール</p>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 border rounded-full text-[10px] uppercase tracking-[0.3em] ewfl-chip-pulse"
            style={{
              background: 'rgba(0,229,255,0.10)',
              borderColor: 'rgba(0,229,255,0.40)',
              color: '#00E5FF',
              boxShadow: '0 0 16px rgba(0,229,255,0.20)',
            }}>
            <span className="w-1.5 h-1.5 bg-[#CCFF00] rounded-full" /> Season 2026/27 live
          </div>
        </div>

        {/* 4 main modes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full max-w-5xl mb-8">
          <div className="card-surface rounded-md p-5 text-center transition-all cursor-pointer group ewfl-shine"
            style={{ borderColor: 'rgba(0,229,255,0.20)' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#00E5FF'; e.currentTarget.style.boxShadow = '0 0 28px rgba(0,229,255,0.30)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(0,229,255,0.20)'; e.currentTarget.style.boxShadow = 'none'; }}
            onClick={() => navigate('/play-now')}
            data-testid="mode-play-now"
          >
            <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-3 relative"
              style={{ background: 'rgba(0,229,255,0.10)' }}>
              <Lightning size={28} weight="fill" style={{ color: '#00E5FF', filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.7))' }} />
            </div>
            <h3 className="font-heading font-bold text-white text-lg uppercase tracking-wide">Play Now</h3>
            <p className="text-xs text-[#7A86B5] mt-1">Quick match between any 2 teams</p>
          </div>

          <div className="card-surface rounded-md p-5 text-center transition-all cursor-pointer group relative ewfl-shine"
            style={{ borderColor: 'rgba(255,0,110,0.22)' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF006E'; e.currentTarget.style.boxShadow = '0 0 28px rgba(255,0,110,0.30)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,0,110,0.22)'; e.currentTarget.style.boxShadow = 'none'; }}
            onClick={() => {
              if (hasLeagueGame) navigate('/league');
              else { onNewGame(); navigate('/league'); }
            }}
            data-testid="mode-league"
          >
            <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-3"
              style={{ background: 'rgba(255,0,110,0.12)' }}>
              <Trophy size={28} weight="fill" style={{ color: '#FF006E', filter: 'drop-shadow(0 0 6px rgba(255,0,110,0.7))' }} />
            </div>
            <h3 className="font-heading font-bold text-white text-lg uppercase tracking-wide">J.League</h3>
            <p className="text-xs text-[#7A86B5] mt-1">
              {hasLeagueGame ? `Season ${gameState.season} · Week ${gameState.currentWeek}/38` : 'Full 38-week season'}
            </p>
            {hasLeagueGame && (
              <button
                data-testid="new-league-save-btn"
                onClick={(e) => { e.stopPropagation(); startNewLeague(); }}
                className="mt-3 text-[10px] font-heading font-bold uppercase tracking-wider text-[#FF006E] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                <ArrowCounterClockwise size={12} /> New Save
              </button>
            )}
          </div>

          <div className="card-surface rounded-md p-5 text-center transition-all cursor-pointer group relative ewfl-shine"
            style={{ borderColor: 'rgba(204,255,0,0.25)' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#CCFF00'; e.currentTarget.style.boxShadow = '0 0 28px rgba(204,255,0,0.30)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(204,255,0,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
            onClick={() => navigate('/manager')}
            data-testid="mode-manager"
          >
            <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-3"
              style={{ background: 'rgba(204,255,0,0.12)' }}>
              <Crown size={28} weight="fill" style={{ color: '#CCFF00', filter: 'drop-shadow(0 0 6px rgba(204,255,0,0.7))' }} />
            </div>
            <h3 className="font-heading font-bold text-white text-lg uppercase tracking-wide">Road to Glory</h3>
            <p className="text-xs text-[#7A86B5] mt-1">
              {hasManagerGame ? 'Continue manager career' : 'Pick a team, manage multi-season'}
            </p>
            {hasManagerGame && (
              <button
                data-testid="new-manager-save-btn"
                onClick={(e) => { e.stopPropagation(); startNewManager(); }}
                className="mt-3 text-[10px] font-heading font-bold uppercase tracking-wider text-[#FF006E] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                <ArrowCounterClockwise size={12} /> New Save
              </button>
            )}
          </div>

          <div className="card-surface rounded-md p-5 text-center transition-all cursor-pointer group relative ewfl-shine"
            style={{ borderColor: 'rgba(122,0,255,0.25)' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7A00FF'; e.currentTarget.style.boxShadow = '0 0 28px rgba(122,0,255,0.30)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(122,0,255,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
            onClick={() => navigate(hasCareerGame ? '/player-career' : '/player-career/select')}
            data-testid="mode-player-career"
          >
            <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-3"
              style={{ background: 'rgba(122,0,255,0.12)' }}>
              <SoccerBall size={28} weight="fill" style={{ color: '#A66BFF', filter: 'drop-shadow(0 0 6px rgba(122,0,255,0.7))' }} />
            </div>
            <h3 className="font-heading font-bold text-white text-lg uppercase tracking-wide">Player Career</h3>
            <p className="text-xs text-[#7A86B5] mt-1">
              {hasCareerGame ? `${careerState.playerName} · Age ${careerState.playerAge || '—'}` : 'Pick a player, live the career'}
            </p>
            {hasCareerGame && (
              <button
                data-testid="new-career-save-btn"
                onClick={(e) => { e.stopPropagation(); startNewCareer(); }}
                className="mt-3 text-[10px] font-heading font-bold uppercase tracking-wider text-[#FF006E] hover:text-white transition-colors inline-flex items-center gap-1"
              >
                <ArrowCounterClockwise size={12} /> New Player
              </button>
            )}
          </div>
        </div>

        {/* Secondary actions */}
        <div className="flex flex-wrap gap-3 w-full max-w-3xl justify-center">
          <Button data-testid="nav-teams-btn" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/teams'); }} variant="outline" className="bg-transparent border-[#1E2240] text-white hover:bg-[#00E5FF] hover:text-black font-heading uppercase tracking-wide">
            <Users size={18} className="mr-2" /> Teams
          </Button>
          <Button data-testid="nav-stats-btn" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/stats'); }} variant="outline" className="bg-transparent border-[#1E2240] text-white hover:bg-[#00E5FF] hover:text-black font-heading uppercase tracking-wide">
            <GameController size={18} className="mr-2" /> Stats
          </Button>
          <Button data-testid="nav-cup-btn" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/cup'); }} variant="outline" className="bg-transparent border-[#1E2240] text-white hover:bg-[#00E5FF] hover:text-black font-heading uppercase tracking-wide">
            <Trophy size={18} className="mr-2" /> Emperor's Cup
          </Button>
          <Button data-testid="nav-save-btn" onClick={() => navigate('/save')} variant="outline" className="bg-transparent border-[#1E2240] text-white hover:bg-[#00E5FF] hover:text-black font-heading uppercase tracking-wide">
            <FloppyDisk size={18} className="mr-2" /> Save/Load
          </Button>
        </div>

        {/* Competition tiles — J.League, Asian Cup (top 4), FIFA CWC (top 2), FIFA WC, trophies, Blue Lock */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 w-full max-w-5xl mt-6">
          <Tile color="#FF006E" Icon={Trophy} label="Emperor's Cup" testid="tile-cup" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/cup'); }} />
          <Tile color="#CCFF00" Icon={Globe} label="Asian Cup" testid="tile-asian-cup" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/asian-cup'); }} />
          <Tile color="#FFB400" Icon={Flag} label="Club WC" testid="tile-cwc" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/club-world-cup'); }} />
          <Tile color="#FF4D00" Icon={GlobeHemisphereWest} label="World Cup" testid="tile-wc" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/world-cup'); }} />
          <Tile color="#00E5FF" Icon={Medal} label="Trophies" testid="tile-trophies" onClick={() => { if (!gameState?.teams?.length) onNewGame(); navigate('/trophies'); }} />
          {/* Blue Lock - locked */}
          <div
            onClick={() => navigate('/blue-lock')}
            data-testid="tile-bluelock"
            className="card-surface rounded-md p-4 text-center transition-all cursor-pointer relative overflow-hidden group"
            style={{ borderColor: 'rgba(255,180,0,0.45)', opacity: 0.85 }}
          >
            <div className="absolute inset-0 opacity-15 pointer-events-none"
              style={{ backgroundImage: 'repeating-linear-gradient(45deg, #FFB400 0 8px, transparent 8px 18px)' }} />
            <Lock size={24} weight="fill" style={{ color: '#FFB400' }} className="mx-auto mb-2 relative" />
            <h4 className="font-heading font-bold text-white text-sm uppercase relative">Blue Lock</h4>
            <span className="absolute top-1 right-1 text-[7px] font-bold tracking-[0.18em] px-1 py-0.5 rounded-sm uppercase"
              style={{ color: '#FFB400', border: '1px solid #FFB400' }}>SOON</span>
          </div>
        </div>

        <div className="mt-10 text-center text-[#5A6390] text-sm">
          <p>20 JWFL Clubs · 25 Players Each · 32 CWC Teams · 32 Asian Cup Teams · 32 World Cup Nations</p>
          <p className="mt-1 text-[#4A5278]">J.League · Emperor's Cup · Asian Cup · FIFA Club World Cup · FIFA World Cup · Glory · Career</p>
        </div>
      </div>
    </div>
  );
};

const Tile = ({ color, Icon, label, testid, onClick }) => (
  <div className="card-surface rounded-md p-4 text-center transition-all cursor-pointer relative"
    style={{ borderColor: `${color}30` }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; e.currentTarget.style.boxShadow = `0 0 24px ${color}40`; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${color}30`; e.currentTarget.style.boxShadow = 'none'; }}
    onClick={onClick}
    data-testid={testid}
  >
    <Icon size={24} weight="fill" style={{ color, filter: `drop-shadow(0 0 6px ${color}99)` }} className="mx-auto mb-2" />
    <h4 className="font-heading font-bold text-white text-sm uppercase">{label}</h4>
  </div>
);
